/**
 * Email normalization utility for frontend
 * Ensures consistent email format across account creation and login
 * 
 * IMPORTANT: This function preserves intentional dots in email addresses.
 * It only normalizes for consistency (lowercase, trim, remove consecutive dots).
 * It NEVER modifies the email in a way that would change the actual email address.
 */

/**
 * Normalize email address for consistent storage and lookup
 * @param {string} email - Email address to normalize
 * @returns {string} - Normalized email address
 * 
 * Rules:
 * - Convert to lowercase
 * - Remove leading/trailing whitespace
 * - Remove ONLY consecutive dots (e.g., user..name@example.com → user.name@example.com)
 * - PRESERVE all single dots in local part (including dots before @ symbol)
 * - PRESERVE intentional dots like aniket.ashis@gmail.com
 */
export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return email;
  }

  // Convert to lowercase
  let normalized = email.toLowerCase();

  // Remove leading and trailing whitespace
  normalized = normalized.trim();

  // Split into local part (before @) and domain part (after @)
  const parts = normalized.split('@');
  
  if (parts.length !== 2) {
    // Invalid email format, return as-is (will be caught by validation)
    return normalized;
  }

  const [localPart, domain] = parts;

  // Remove ONLY consecutive dots in local part (e.g., user..name → user.name)
  // But preserve single dots (e.g., aniket.ashis stays as aniket.ashis)
  const normalizedLocalPart = localPart.replace(/\.{2,}/g, '.');

  // Reconstruct email
  normalized = `${normalizedLocalPart}@${domain}`;

  return normalized;
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmailFormat = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Basic email regex - allows dots in local part
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Sanitize email for display (same as normalize but with additional safety)
 * @param {string} email - Email address to sanitize
 * @returns {string} - Sanitized email address
 */
export const sanitizeEmail = (email) => {
  return normalizeEmail(email);
};


